import React, { useState, useEffect } from "react"
import _ from 'lodash'
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"

import automationService from "data/automation"

import { alphanum } from "components/table/lib/sort"
import { field as _field } from "components/table/lib/column-field"
import { DropdownInput } from "components/table/carousel/inputs"
import { NumberEdit } from "components/table/inputs"
import { PercentLineHeader_s, PercentLineTable_s } from "components/line-items/controls/line-item-carousel-styles"
import { Label_s } from 'styles/app-styles'

type PercentLineProps = {
  rowData: RowData
  field: string
  namedItems: {lineItems: [], systemLineItems: [], LineItemTypeOptions: [], propType: string}
  persist: Function
}

type TableRow = Array<{
  lineItemId: number
  codeName: string
  selected: boolean
  percent: number
  system: boolean
  type: string
}>

type RowData = {
  lineitemid: number
  lineitemdetail: {
    items: TableRow
  }
}

const PercentLine = (props: PercentLineProps) => {
  const initialItems = props['rowData'][props.field] && props['rowData'][props.field].state !== 'percentLineItem' ? [] :
    _.isArray(_.get(props.rowData[props.field], "items")) ? _.get(props.rowData[props.field], "items") : []
  const emptyRecord: RowData = {
    lineitemid: -1,
    lineitemdetail: {
      items: []
    }
  }
  let lineItemTypeOptions = []
  let hospitalityTypes = []
  if(props.namedItems.propType === "HOSPITALITY") {
    hospitalityTypes = _.concat(
      _.get(props, "namedItems.LineItemTypeOptions"),
      { prefix: 'Dept. Revenue', value: 'hosp_dept_revenue'},
      { prefix: 'Dept. Expense', value: 'hosp_dept_expense'},
    )
    lineItemTypeOptions = _.concat(
      { label: 'All', value: 'all' },
      { label: 'System Line Items', value: 'system', prefix: 'System' },
      { label: 'Line Items', value: 'line_items' },
      { label: 'Departmental Line Items', value: 'departmental_line_items' },
    )
  } else {
    lineItemTypeOptions = _.concat(
      { label: 'All', value: 'all' },
      _.get(props, "namedItems.LineItemTypeOptions"),
      { label: 'System Line Items', value: 'system', prefix: 'System' }
    )
  }
  const hasNonZeroItems = initialItems.length ? true : false
  const [data, setData] = useState<RowData|null>(props.rowData)
  const [tableRows, setTableRows] = useState<[]>(normalizeLineItems(props.namedItems.lineItems, props.namedItems.systemLineItems))
  const [sortOrder, setSortOrder] = useState<number>(0)
  const [selectedLineItemType, setSelectedLineItemType] = useState<string>(hasNonZeroItems ? "all" : _.find(lineItemTypeOptions, opt => opt.value === "expense") ? "expense" : _.get(_.nth(lineItemTypeOptions, 1), "value"))
  const [displayAll, setDisplayAll] = useState<boolean>(hasNonZeroItems ? false : true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const field = _field('lineitem')

  useEffect(() => {
    if (!props['rowData'][props.field] || props['rowData'][props.field].state !== 'percentLineItem') {
      setData(emptyRecord)
    }
  }, [props['rowData']])

  useEffect(() => {
    setTableRows(normalizeLineItems(props.namedItems.lineItems, props.namedItems.systemLineItems))
  }, [data, selectedLineItemType, displayAll, searchTerm, props.rowData, sortOrder])

  const updateItems = _.debounce((percent, rowData) => {
    let localCache = _.cloneDeep(data);
    let nextData = _.cloneDeep(props.rowData)
    //clear items on first persist
    if(props['rowData'][props.field] === null || props['rowData'][props.field].state !== 'percentLineItem') {
      _.set(nextData, "lineitemdetail.items", [])
    }
    _.set(nextData, "lineitemdetail.state", "percentLineItem")

    let items = _.get(nextData, "lineitemdetail.items")
    if(percent === 0 || percent === null) {
      items = _.filter(items, i => i.lineItemId !== rowData.lineItemId)
    } else {
      const newItem = {system: _.includes(rowData.codeName, "Dept.") ? false : rowData.system, lineItemId: rowData.lineItemId, percent}
      const idx = _.findIndex(items, i => i.lineItemId === newItem.lineItemId)
      if(idx === -1) items.push(newItem)
      else items.splice(idx, 1, newItem)
    }

    _.set(nextData, "lineitemdetail.items", items)
    _.set(localCache, "lineitemdetail.items", items)
    setData(localCache)

    nextData["inputtype"] = "percentLineItem"
    nextData["amt"] = null
    nextData["amttype"] = null
    nextData["areaid"] = null
    nextData["inflationdetail"] = null

    delete nextData.lineitemdetail.inflation
    delete nextData.lineitemdetail.lineItemId
    delete nextData.lineitemdetail.areaMeasureNamedItemId
    delete nextData.lineitemdetail.percent
    delete nextData.lineitemdetail.revenue
    delete nextData.lineitemdetail.inflateFromAnalysisBegin

    props.persist(nextData, props.field, props.rowData)
    // refocus on first table cell if non-zero view is selected and one of the rows was removed
    if(!displayAll && (percent === 0 || percent === null)) {
      setTimeout(() => {
        automationService.broadcast("REFOCUS", {})
      }, 400)
    }
  }, 200)

  // Given the line items and system line items from redux state
  // this function will output the data scruture that matches the column
  // configuration above with metadata added for filtering purposes.
  function normalizeLineItems(lineItems, systemLineItems): [] {
    let tableRows = []
    const currentLineItemId = data && data.lineitemid !== -1 ? data.lineitemid : props.rowData.lineitemid

    if (lineItems.length || systemLineItems.length) {
      // get a list of currently selected line items for the current line item if any are already selected
      const currentLineItem = data && data.lineitemid !== -1 ? data : props.rowData
      let selectedLineItems: TableRow = []
      if (currentLineItem && currentLineItem.lineitemdetail && currentLineItem.lineitemdetail.items) {
        selectedLineItems = currentLineItem.lineitemdetail.items
      }
      // normalize the user generated line items
      const normalizedLineItems = _.map(lineItems, item => {
        // determine if the current line item has already been selected
        const selected = _.find(selectedLineItems, selected => selected.lineItemId === item.lineitemid && selected.system === false)
        const prefix = props.namedItems.propType === "HOSPITALITY" ?
                       _.get(_.find(hospitalityTypes, type => type.value === item.type), "prefix") :
                       _.get(_.find(lineItemTypeOptions, type => type.value === item.type), "prefix")
        return {
          lineItemId: item.lineitemid,
          codeName: `${prefix}: ${item.code} - ${item.name?.slice(0, 26) ?? ''}`,
          selected: selected ? true : false,
          percent: selected ? selected.percent : null,
          system: _.includes(item.code, "*") ? true : false,
          type: _.includes(item.code, "*")  ? "system" : item.type
        };
      })
      // normalize the system line items
      const normalizedSystemItems = _.map(systemLineItems, item => {
        // determine if the current line item has already been selected
        const selected = _.find(selectedLineItems, selected => selected.lineItemId === item.systemlineitemid && selected.system === true)
        return {
          lineItemId: item.systemlineitemid,
          codeName: `System: ${item.code} - ${item.name?.slice(0, 26) ?? ''}`,
          selected: selected ? true : false,
          percent: selected ? selected.percent : null,
          system: true,
          type: 'system'
        }
      })
      // concatenate the two normalized arrays
      tableRows = normalizedSystemItems.concat(normalizedLineItems)
    }
    // add current lineitemid to all objects for validation lookup
    _.forEach(tableRows, r => r['lineitemid'] = data && data.lineitemid)

    // filter line items based on the currently selected line item type if 'all' is not selected
    if (selectedLineItemType !== 'all') {
      if (selectedLineItemType === 'line_items') {
        tableRows = _.filter(tableRows, item => item.type !== "hosp_dept_revenue" && item.type !== "hosp_dept_expense" && item.type !== "system")
      } else if (selectedLineItemType === 'departmental_line_items') {
        tableRows = _.filter(tableRows, item => item.type === "hosp_dept_revenue" || item.type === "hosp_dept_expense" || (item.type === "system" && _.includes(item.codeName, "Dept.")))
      } else {
        tableRows = _.filter(tableRows, item => item.type === selectedLineItemType)
      }
    }
    // filter line items based on the current search term
    if (searchTerm) {
      tableRows = _.filter(tableRows, item => item.codeName.toLowerCase().includes(searchTerm.toLowerCase()))
    }
    // filter line items based on show all or selected
    if (!displayAll) {
      tableRows = _.filter(tableRows, item => item.selected === true)
    }
    // sort table rows appropriately
    tableRows.sort(comparator("codeName"))
    if(sortOrder === 1) tableRows = _.reverse(tableRows)
    // never return the current line item as part of the selectable list of line items
    return _.filter(tableRows, item => item.lineItemId !== currentLineItemId)
  }

  function comparator(field) {
    const get = (x) => _.get(x, field)
    return (a,b) => alphanum(get(a), get(b))
  }

  return (
    <div>
      <PercentLineHeader_s>
        <div>
          <label>Line Item Type</label>
          <DropdownInput className="automated" data={{value: selectedLineItemType}} field="value" options={lineItemTypeOptions} persist={val => setSelectedLineItemType(val)} default={selectedLineItemType} />
        </div>
      </PercentLineHeader_s>
      <PercentLineHeader_s className="percent-line-horizontal">
        <div>
          <label>Search</label>
          <input type="text" className="automated" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div>
          <label>Display</label>
          <div className="percent-line-radio-buttons">
            <label>
              <input
                type="radio"
                className="automated"
                name="state"
                value="all"
                onClick={() => setDisplayAll(true)}
                checked={displayAll === true}/>
              All
            </label>
            <label>
              <input
                type="radio"
                className="automated"
                name="state"
                value="nonzero"
                onClick={() => setDisplayAll(false)}
                checked={displayAll == false}/>
              Non-zero
            </label>
          </div>
        </div>
      </PercentLineHeader_s>
      <PercentLineTable_s>
        <table className="p-datatable" id="percent-line-table">
          <thead className="p-datatable-thead">
            <th style={{width: "200px"}} className="p-sortable-column" onClick={() => { sortOrder === 0 ? setSortOrder(1) : setSortOrder(0) }}>
              <span className="p-column-title">Line Item</span>
              <span className="p-sortable-column-icon pi pi-fw pi-sort-alt"></span>
            </th>
            <th style={{width: "50px"}}>
              <Label_s>Percent</Label_s>
            </th>
          </thead>
          <tbody className="p-datatable-tbody">
            {
              _.map(tableRows, row => {
                return (
                  <tr className="p-datatable-row">
                    <td style={{width: "200px"}}>
                      {row.codeName}
                    </td>
                    <td style={{width: "50px"}}>
                      <NumberEdit
                        key={row.codeName}
                        rowData={row}
                        field="percent"
                        className="automated"
                        persist={(val, _field) => updateItems(val, row)}
                        writePrecision={4} />
                    </td>
                  </tr>
                )
              })
            }
            <td></td>
          </tbody>
        </table>
      </PercentLineTable_s>
    </div>
  )
}

export default PercentLine
